import React, { useState } from 'react';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import { translate } from '../../../services/translate';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import useFilters from '../../../components/Filter/hooks/useFilters';
import { CitizenParamList } from '../../../typings/Params';
import { RouteProp, useRoute } from '@react-navigation/native';
import FilterInput from '../../../components/Filter/components/FilterInput';
import { NativeSyntheticEvent, TextInputKeyPressEventData, View } from 'react-native';
import { Pagination } from '../../../components/Pagination';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/Auth';
import { citizenService } from '../../../services/central-api/citizen';
import MyDataTable from '../../../components/MyDataTable';
import Badge, { BadgeColor } from '../../../components/Badge';
import CitizenManagementModal from './CitizenManagementModal';
import { applyMask } from '../../../components/helpers/documents';
import DropdownButton, { Action } from '../../../components/DropdownButton';
import Toast from 'react-native-toast-message';
import Filter from './Filters';
import DeactivateCitizenModal from './DeactivateCitizenModal';
import { RootNavigation } from '../../../Routes';
import DeleteModal from '../../../components/DeleteModal';

export default function CitizenManagement({ navigation }: { navigation: RootNavigation; }) {
    const { user: authenticatedUser } = useAuth();
    const route = useRoute<RouteProp<CitizenParamList, 'CitizenManagement'>>();

    const [filters, setFilters] = useFilters<CitizenParamList['CitizenManagement']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [showCitizenModal, setShowCitizenModal] = useState<boolean>(false);
    const [selectedCitizen, setSelectedCitizen] = useState<BaseCitizen>();
    const [performingActionForRow, setPerformingActionForRow] = useState<number[]>([]);
    const [showDeactivateCitizenModal, setShowDeactivateCitizenModal] = useState<boolean>(false);
    const [showDeleteCitizenModal, setShowDeleteCitizenModal] = useState<boolean>(false);

    const queryResult = useQuery({
        queryKey: ['citizensTable', filters],
        queryFn: () => citizenService.getCitizens(filters!),
        enabled: !!authenticatedUser,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    function getCitizenStatus(deactivate: string | null, verified: boolean) {
        let customName = 'active';
        let badgeColor: BadgeColor = 'green';

        if (deactivate) {
            customName = 'disabled_alt';
            badgeColor = 'red';
        } else if (!verified) {
            customName = 'waiting';
            badgeColor = 'orange';
        }

        return <View style={[{ justifyContent: 'center' }]}>
            <Badge mode='SQUARE' size='medium' color={badgeColor}>{translate(customName)}</Badge>
        </View>;
    }

    async function toggleCitizenActivation(citizenId: number) {
        try {
            await citizenService.toggleCitizenActivation(citizenId);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            refetch();
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    async function deleteCitizen(citizenId: number) {
        try {
            await citizenService.deleteCitizen(citizenId);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            refetch();
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return (<Container>
        <Header
            title={translate('citizenManagement')}
        />
        <Card>
            <DataTableHeader
                title={translate('registeredCitizens')}
                filter={<Filter
                    filters={filters}
                    setFilters={setFilters}
                    textFilter={textFilter}
                    setTextFilter={setTextFilter}
                />}
                zIndex={2}
                filterInput={<FilterInput
                    value={textFilter}
                    placeholder={translate('typeToSearch')}
                    onChange={setTextFilter}
                    onKeyPress={handleKeyDown}
                />}
            />
            <DataTableContainer
                dataTable={<MyDataTable
                    columns={[
                        {
                            name: translate('status'),
                            cell: row => getCitizenStatus(row.deactivate, row.verified),
                            grow: 0,
                        },
                        {
                            name: translate('fullName'),
                            cell: row => row.name,
                        },
                        {
                            name: translate('document'),
                            cell: row => applyMask(row.document, 'cpf')
                        },
                        {
                            name: translate('phone'),
                            cell: row => applyMask(row.phone, 'phone')
                        },
                        {
                            name: '',
                            button: true,
                            cell: row => {
                                const actions: Action[] = [];
                                actions.push({
                                    content: translate(!row.deactivate ? 'editInformation' : 'viewInformation'),
                                    onPress: () => {
                                        setSelectedCitizen(row);
                                        setShowCitizenModal(true);
                                    }
                                });

                                if (authenticatedUser?.permissions.citizen_management_banishment) {
                                    actions.push({
                                        content: translate(!row.deactivate ? 'deactivateCitizen' : 'activateCitizen'),
                                        variant: !row.deactivate ? 'danger' : 'primary',
                                        onPress: async () => {
                                            if (!row.deactivate) {
                                                setSelectedCitizen(row);
                                                setShowDeactivateCitizenModal(true);
                                            } else {
                                                try {
                                                    setPerformingActionForRow([
                                                        ...performingActionForRow,
                                                        row.id
                                                    ]);
                                                    await toggleCitizenActivation(row.id);
                                                } finally {
                                                    setPerformingActionForRow(performingActionForRow.filter(id => id != row.id));
                                                }
                                            }
                                        },
                                    }, {
                                        content: translate('delete'),
                                        variant: 'danger',
                                        onPress: () => {
                                            setSelectedCitizen(row);
                                            setShowDeleteCitizenModal(true);
                                        }
                                    });
                                }
                                return (
                                    <DropdownButton
                                        size={180}
                                        actions={actions}
                                        isLoading={performingActionForRow.includes(row.id)}
                                    />
                                );
                            }
                        }
                    ]}
                    data={data?.rows || []}
                    progressPending={isLoading}
                />}
                pagination={<Pagination
                    currentPage={filters.page}
                    totalItems={data?.count || 0}
                    setPage={page => {
                        setFilters(old => ({ ...old, page }));
                        navigation.setParams({ ...route.params, page: page + 1 });
                    }}
                    pageSize={filters.limit}
                    setPageSize={pageSize => {
                        setFilters(old => ({ ...old, limit: pageSize, page: 0 }));
                        navigation.setParams({ ...route.params, limit: pageSize, page: 1 });
                    }}
                />}
            />
        </Card>
        {selectedCitizen && <DeactivateCitizenModal
            citizenId={selectedCitizen.id}
            isModalVisible={showDeactivateCitizenModal}
            setModalVisible={setShowDeactivateCitizenModal}
            onConfirm={refetch}
        />}
        {selectedCitizen && <CitizenManagementModal
            isModalVisible={showCitizenModal}
            setModalVisible={setShowCitizenModal}
            citizenId={selectedCitizen.id}
            onConfirm={refetch}
        />}
        {selectedCitizen &&
            <DeleteModal
                setModalVisible={setShowDeleteCitizenModal}
                isModalVisible={showDeleteCitizenModal}
                itemName={selectedCitizen.name}
                onSubmit={async () => deleteCitizen(selectedCitizen.id)}
            />
        }
    </Container>);
}
